import React, { useState, useEffect } from 'react';
import { FiX, FiChevronDown, FiChevronUp } from 'react-icons/fi';

export default function RoleForm({ isOpen, onClose, onSubmit, initialData, permissions }) {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    permissions: []
  });
  const [loading, setLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        code: initialData.code || '',
        description: initialData.description || '',
        permissions: initialData.permissions?.map(p => p._id) || []
      });
    } else {
      setFormData({
        name: '',
        code: '',
        description: '',
        permissions: []
      });
    }
  }, [initialData]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'name') {
      const code = value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
      setFormData({ ...formData, name: value, code });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handlePermissionToggle = (permId) => {
    setFormData({
      ...formData,
      permissions: formData.permissions.includes(permId)
        ? formData.permissions.filter(id => id !== permId)
        : [...formData.permissions, permId]
    });
  };

  const handleSelectAll = (resource) => {
    const resourcePermissions = permissions.filter(p => p.resource === resource).map(p => p._id);
    const allSelected = resourcePermissions.every(id => formData.permissions.includes(id));
    
    if (allSelected) {
      setFormData({
        ...formData,
        permissions: formData.permissions.filter(id => !resourcePermissions.includes(id))
      });
    } else {
      setFormData({
        ...formData,
        permissions: [...new Set([...formData.permissions, ...resourcePermissions])]
      });
    }
  };

  const toggleGroup = (resource) => {
    setExpandedGroups({
      ...expandedGroups,
      [resource]: !expandedGroups[resource]
    });
  };

  const groupedPermissions = permissions.reduce((groups, perm) => {
    if (!groups[perm.resource]) groups[perm.resource] = [];
    groups[perm.resource].push(perm);
    return groups;
  }, {});

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await onSubmit(formData);
    setLoading(false);
    onClose();
  };

  const isAllSelected = (resource) => {
    const resourcePermissions = permissions.filter(p => p.resource === resource);
    return resourcePermissions.length > 0 && 
      resourcePermissions.every(p => formData.permissions.includes(p._id));
  };

  const isAnySelected = (resource) => {
    const resourcePermissions = permissions.filter(p => p.resource === resource);
    return resourcePermissions.some(p => formData.permissions.includes(p._id));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white">
          <h2 className="text-xl font-semibold">
            {initialData ? 'Edit Role' : 'Create New Role'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <FiX className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Role Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="e.g., Operations Manager"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Role Code</label>
              <input
                type="text"
                name="code"
                value={formData.code}
                onChange={handleChange}
                placeholder="Auto-generated from name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
              />
              <p className="text-xs text-gray-500 mt-1">Unique identifier for the role</p>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={2}
                placeholder="What does this role do?"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>

          {/* Permissions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Permissions</label>
            <p className="text-xs text-gray-500 mb-3">Select the permissions for this role</p>
            
            <div className="border rounded-lg overflow-hidden">
              {Object.entries(groupedPermissions).map(([resource, perms]) => (
                <div key={resource} className="border-b last:border-0">
                  <button
                    type="button"
                    onClick={() => toggleGroup(resource)}
                    className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition"
                  >
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={isAllSelected(resource)}
                        ref={el => el && (el.indeterminate = !isAllSelected(resource) && isAnySelected(resource))}
                        onChange={() => handleSelectAll(resource)}
                        onClick={(e) => e.stopPropagation()}
                        className="mr-3"
                      />
                      <span className="font-medium text-gray-700 capitalize">
                        {resource.replace('_', ' ')}
                      </span>
                      <span className="ml-2 text-xs text-gray-500">
                        ({perms.length} permissions)
                      </span>
                    </div>
                    {expandedGroups[resource] ? (
                      <FiChevronUp className="text-gray-400" />
                    ) : (
                      <FiChevronDown className="text-gray-400" />
                    )}
                  </button>
                  
                  {expandedGroups[resource] && (
                    <div className="p-3 grid grid-cols-2 gap-2">
                      {perms.map(perm => (
                        <label key={perm._id} className="flex items-center text-sm">
                          <input
                            type="checkbox"
                            checked={formData.permissions.includes(perm._id)}
                            onChange={() => handlePermissionToggle(perm._id)}
                            className="mr-2"
                          />
                          <span>{perm.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 transition"
            >
              {loading ? 'Saving...' : initialData ? 'Update Role' : 'Create Role'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
